import { useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { useMutation } from '@apollo/client/react'
import { formatISO } from 'date-fns'

import SettingContainer from 'js/admin/layout/SettingContainer'
import ActionContainer from 'js/components/ActionContainer/ActionContainer'
import DateTimeField from 'js/components/DateTimeField'
import DeleteModal from 'js/components/EntityActions/DeleteModal'
import ScheduleFields from 'js/components/EntityActions/Schedule/components/ScheduleFields'
import FormItem from 'js/components/Form/FormItem'
import { Col, Container, Row } from 'js/components/Grid'
import HideVisually from 'js/components/HideVisually/HideVisually'
import TabMenu from 'js/components/Navigation/TabMenu'
import TabPanel from 'js/components/Navigation/TabPanel'
import Setting from 'js/components/Setting'
import Spacer from 'js/components/Spacer/Spacer'
import Sticky from 'js/components/Sticky/Sticky'
import getUrls from 'js/magazine/lib/getUrls'
import {
    ADD_MAGAZINE_ISSUE,
    EDIT_MAGAZINE_ISSUE,
} from 'js/magazine-issue/AddEdit/AddEditForm/AddEditForm.queries'
import refetchQueries from 'js/magazine-issue/lib/refetchQueries'

import styles from './AddEditForm.module.css'
import Articles from './components/Articles'
import Settings from './components/Settings'

type AddEditFormProps = {
    containerGuid?: string
    entity?: {
        guid: string
        url?: string
        statusPublished?: string
        timePublished?: string | null
        scheduleDeleteEntity?: string
        issueNumber?: number
        backgroundColor?: string
        accessId?: string
        articles?: { guid: string }[]
        colophon?: { key: string; label: string; value: string }[]
    } | null
    title: string
}

const AddEditForm = ({ containerGuid, entity, title }: AddEditFormProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()

    const urls = getUrls()

    const isPublished = entity?.statusPublished === 'published'

    const [tab, setTab] = useState('general')

    const [timeScheduled, setTimeScheduled] = useState<string | null>(
        isPublished ? null : entity?.timePublished || null,
    )
    const [timePublished, setTimePublished] = useState<string | undefined>()
    const [scheduleDeleteEntity, setScheduleDeleteEntity] = useState(
        entity?.scheduleDeleteEntity || '',
    )

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const toggleDeleteModal = () => setShowDeleteModal(!showDeleteModal)

    const afterDelete = () => {
        navigate(urls.list, {
            replace: true,
        })
    }

    const handleClose = () => {
        if (location?.state?.prevPathname) {
            navigate(location.state.prevPathname, {
                replace: true,
            })
        } else if (entity?.url) {
            navigate(entity.url, {
                replace: true,
                state: {
                    prevPathname: location.state?.prevPathname,
                },
            })
        } else {
            navigate(urls.list, {
                replace: true,
            })
        }
    }

    const handleSaveAsDraft = () => {
        setValue('isDraft', true)
        submitForm()
    }

    const handlePublish = () => {
        setValue('isDraft', false)
        submitForm()
    }

    const [addIssue] = useMutation(ADD_MAGAZINE_ISSUE)
    const [editIssue] = useMutation(EDIT_MAGAZINE_ISSUE)

    const submit = async ({
        isDraft,
        issueNumber,
        backgroundColor,
        accessId,
        articles,
        colophon,
    }: any) => {
        const mutate = entity ? editIssue : addIssue

        await mutate({
            variables: {
                input: {
                    ...(entity
                        ? {
                              guid: entity.guid,
                              articles: articles.map(
                                  ({ guid }: { guid: string }) => guid,
                              ),
                              isAccessRecursive: true,
                          }
                        : {
                              containerGuid,
                              subtype: 'magazine_issue',
                          }),
                    // Schedule or save as draft
                    ...(isDraft
                        ? {
                              timePublished:
                                  timePublished || timeScheduled || null,
                          }
                        : {}),
                    // Publish with a chosen publication date
                    ...(!isDraft && timePublished ? { timePublished } : {}),
                    // Publish now when leaving draft / creating without a date
                    ...(!isDraft &&
                    !timePublished &&
                    (!entity || entity.statusPublished === 'draft')
                        ? {
                              timePublished: formatISO(new Date()),
                          }
                        : {}),
                    accessId,
                    scheduleDeleteEntity: scheduleDeleteEntity || null,
                    issueNumber,
                    backgroundColor,
                    colophon: colophon.map(
                        ({ key, value }: { key: string; value: string }) => ({
                            key,
                            value,
                        }),
                    ),
                },
            },
            refetchQueries,
        })
            .then(({ data }: any) => {
                if (entity) {
                    navigate(data.editEntity.entity.url, {
                        replace: true,
                        state: {
                            prevPathname: location.state?.prevPathname,
                        },
                    })
                } else {
                    navigate(data.addEntity.entity.url, {
                        replace: true,
                        state: {
                            prevPathname: location.state?.prevPathname,
                        },
                    })
                }
            })
            .catch((errors: unknown) => {
                console.error(errors)
            })
    }

    const defaultValues = {
        isDraft: true,
        issueNumber: entity?.issueNumber || 1,
        backgroundColor: entity?.backgroundColor || '',
        accessId: entity?.accessId,
        articles: entity?.articles || [],
        colophon: entity?.colophon || [],
    }

    const formMethods = useForm({
        mode: 'onChange',
        defaultValues,
    })

    const {
        control,
        handleSubmit,
        setValue,
        getValues,
        formState: { isValid, isSubmitting },
    } = formMethods

    const submitForm = handleSubmit(submit)

    const { fields } = useFieldArray({
        control,
        name: 'colophon',
    })

    return (
        <ActionContainer
            title={title}
            timeScheduled={timeScheduled}
            canSaveAsDraft
            saveAsDraft={handleSaveAsDraft}
            publish={handlePublish}
            isValid={isValid}
            isSavingAsDraft={isSubmitting && getValues('isDraft')}
            isPublishing={isSubmitting && !getValues('isDraft')}
            isPublished={isPublished}
            onDelete={entity ? toggleDeleteModal : null}
            onClose={handleClose}
        >
            <FormProvider {...formMethods}>
                <HideVisually aria-hidden>
                    <FormItem
                        control={control}
                        type="switch"
                        name="isDraft"
                        tabIndex={-1}
                    />
                </HideVisually>

                <Sticky className={styles.tabMenuWrapper}>
                    <Container size="small">
                        <TabMenu
                            label={t('entity-magazine.issue-options')}
                            options={[
                                {
                                    onClick: () => setTab('general'),
                                    label: t('global.general'),
                                    isActive: tab === 'general',
                                    id: 'tab-general',
                                    ariaControls: 'tabpanel-general',
                                },
                                {
                                    onClick: () => setTab('settings'),
                                    label: t('global.settings'),
                                    isActive: tab === 'settings',
                                    id: 'tab-settings',
                                    ariaControls: 'tabpanel-settings',
                                },
                                ...(entity?.articles?.length
                                    ? [
                                          {
                                              onClick: () =>
                                                  setTab('table-of-contents'),
                                              label: t(
                                                  'entity-magazine.table-of-contents',
                                              ),
                                              isActive:
                                                  tab === 'table-of-contents',
                                              id: 'tab-table-of-contents',
                                              ariaControls:
                                                  'tabpanel-table-of-contents',
                                          },
                                      ]
                                    : []),
                                {
                                    onClick: () => setTab('schedule'),
                                    label: t('form.schedule'),
                                    isActive: tab === 'schedule',
                                    id: 'tab-schedule',
                                    ariaControls: 'tabpanel-schedule',
                                },
                                {
                                    onClick: () => setTab('advanced'),
                                    label: t('global.advanced'),
                                    isActive: tab === 'advanced',
                                    id: 'tab-advanced',
                                    ariaControls: 'tabpanel-advanced',
                                },
                            ]}
                            showBorder
                            style={{ position: 'static' }}
                        />
                    </Container>
                </Sticky>

                <Container
                    size="small"
                    style={{
                        paddingTop: 24,
                    }}
                >
                    <TabPanel
                        id={`tabpanel-general`}
                        aria-labelledby={`tab-general`}
                        visible={tab === 'general'}
                    >
                        <Spacer
                            as="form"
                            spacing="small"
                            onSubmit={handleSubmit(submit)}
                        >
                            <FormItem
                                control={control}
                                type="number"
                                name="issueNumber"
                                label={t('entity-magazine.issue-number')}
                                helper={t(
                                    'entity-magazine.issue-number-helper',
                                )}
                                min={1}
                                max={9999}
                                required
                                style={{ width: 180 }}
                            />

                            <FormItem
                                control={control}
                                type="color"
                                name="backgroundColor"
                                title={t('global.background-color')}
                                helper={t(
                                    'entity-magazine.background-color-helper',
                                )}
                                isClearable
                            />

                            {fields.length ? (
                                <>
                                    <Setting
                                        subtitle="entity-magazine.colophon"
                                        helper="entity-magazine.colophon-helper"
                                    />
                                    <Row $spacing={20}>
                                        {fields.map(({ label, id }, index) => {
                                            return (
                                                <Col
                                                    key={id}
                                                    mobileLandscapeUp={1 / 2}
                                                >
                                                    <FormItem
                                                        control={control}
                                                        type="text"
                                                        name={`colophon.${index}.value`}
                                                        label={label}
                                                        // className="ColophonFieldLabel"
                                                    />
                                                </Col>
                                            )
                                        })}
                                    </Row>
                                </>
                            ) : null}
                        </Spacer>
                    </TabPanel>

                    <TabPanel
                        id={`tabpanel-settings`}
                        aria-labelledby={`tab-settings`}
                        visible={tab === 'settings'}
                    >
                        <Settings />
                    </TabPanel>

                    <TabPanel
                        id={`tabpanel-table-of-contents`}
                        aria-labelledby={`tab-table-of-contents`}
                        visible={tab === 'table-of-contents'}
                    >
                        <Articles />
                    </TabPanel>

                    <TabPanel
                        id={`tabpanel-schedule`}
                        aria-labelledby={`tab-schedule`}
                        visible={tab === 'schedule'}
                    >
                        <ScheduleFields
                            {...({
                                canSchedulePublish: !isPublished,
                                timeScheduled,
                                setTimeScheduled,
                                scheduleDeleteEntity,
                                setScheduleDeleteEntity,
                            } as any)}
                        />
                    </TabPanel>

                    <TabPanel
                        id={`tabpanel-advanced`}
                        aria-labelledby={`tab-advanced`}
                        visible={tab === 'advanced'}
                    >
                        <Row $gutter={30}>
                            <Col tabletUp={1 / 2}>
                                <SettingContainer
                                    subtitle="global.published"
                                    helper="form.published-helper"
                                    inputWidth="full"
                                    $mb
                                >
                                    <DateTimeField
                                        name="timePublished"
                                        value={
                                            timePublished ||
                                            entity?.timePublished
                                        }
                                        onChange={(value) =>
                                            setTimePublished(value || undefined)
                                        }
                                        isClearable
                                    />
                                </SettingContainer>
                            </Col>
                        </Row>
                    </TabPanel>
                </Container>
            </FormProvider>

            <DeleteModal
                isVisible={showDeleteModal}
                onClose={toggleDeleteModal}
                title={t('entity-magazine.delete-issue')}
                entity={entity}
                afterDelete={afterDelete}
                refetchQueries={refetchQueries as any}
            />
        </ActionContainer>
    )
}

export { ADD_MAGAZINE_ISSUE }
export default AddEditForm
